import React, { useMemo, useState } from "react";
import { X, MessageCircle, Pencil, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  LOC_WHATSAPP_MESSAGE_TYPES, defaultLocWhatsappType, buildLocWhatsappMessage, buildWhatsappUrl,
  needsParkingStartDate, needsLastDateNoCharge,
} from "@/lib/locWhatsapp";

const inputCls =
  "w-full border border-[#e2e1eb] rounded-md px-2.5 py-1.5 text-[13px] focus:outline-none focus:border-yamaha-400";

/** Phase 2 — "Open WhatsApp" flow. Builds a short prefilled reminder from the
 *  case/row data, lets staff review/edit it, then opens a normal `wa.me`
 *  deep link (WhatsApp app on mobile / WhatsApp Web on desktop) — the actual
 *  send always happens manually inside WhatsApp. After opening, offers an
 *  optional "Mark WhatsApp Sent" log step (can be skipped, and can be
 *  repeated any number of times for the same message). */
export const LocWhatsappModal = ({ caseObj, workshopContact, defaultParkingPerDay, onClose, onMarkSent }) => {
  const [messageType, setMessageType] = useState(() => defaultLocWhatsappType(caseObj));
  const [mobile, setMobile] = useState(caseObj?.mobile_number || "");
  const [parkingPerDay, setParkingPerDay] = useState(caseObj?.parking_charge_per_day || defaultParkingPerDay || "");
  const [parkingStartDate, setParkingStartDate] = useState((caseObj?.parking_charge_start_date || "").slice(0, 10));
  const [lastDateNoCharge, setLastDateNoCharge] = useState(() => {
    const start = caseObj?.parking_charge_start_date;
    if (!start) return "";
    const d = new Date(`${start.slice(0, 10)}T00:00:00`);
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [editing, setEditing] = useState(false);
  const [manualMessage, setManualMessage] = useState(null);
  const [step, setStep] = useState("preview"); // preview | confirm_sent
  const [saving, setSaving] = useState(false);

  const showParkingStartDate = needsParkingStartDate(messageType);
  const showLastDateNoCharge = needsLastDateNoCharge(messageType);

  const autoMessage = useMemo(
    () => buildLocWhatsappMessage(caseObj, messageType, { workshopContact, parkingPerDay, parkingStartDate, lastDateNoCharge }),
    [caseObj, messageType, workshopContact, parkingPerDay, parkingStartDate, lastDateNoCharge],
  );
  const message = manualMessage ?? autoMessage;

  const changeType = (t) => { setMessageType(t); setManualMessage(null); };

  const openWhatsapp = () => {
    if (!mobile || !mobile.replace(/\D/g, "")) {
      toast.error("No valid mobile number to message.");
      return;
    }
    if (!message || !message.trim()) {
      toast.error("Message cannot be empty.");
      return;
    }
    if (showParkingStartDate && !(parkingPerDay && Number(parkingPerDay) > 0)) {
      toast.error("Enter a valid Parking Charge / Day amount.");
      return;
    }
    if (showParkingStartDate && !parkingStartDate) {
      toast.error("Enter the Charges Start Date.");
      return;
    }
    if (showLastDateNoCharge && !lastDateNoCharge) {
      toast.error("Enter the Last Date Without Parking Charges.");
      return;
    }
    window.open(buildWhatsappUrl(mobile, message), "_blank");
    setStep("confirm_sent");
  };

  const markSent = async () => {
    setSaving(true);
    try {
      await onMarkSent({ message_type: messageType, mobile_number: mobile });
      onClose();
    } catch (e) {
      // Error toast already shown by the caller's onMarkSent; keep the
      // dialog open so the user can retry.
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="loc-whatsapp-modal">
      <div className="bg-white rounded-xl border border-[#e2e1eb] w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-3 border-b border-[#e2e1eb] flex items-start gap-2 sticky top-0 bg-white">
          <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
            <MessageCircle className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-[15px] font-semibold text-[#1a1b22]">Open WhatsApp</h3>
            <div className="text-[11.5px] text-[#747684] mt-0.5">
              {caseObj?.customer_name} · {caseObj?.registration_number || caseObj?.vin_no || ""}
            </div>
          </div>
          <button onClick={onClose} data-testid="loc-whatsapp-close-btn" className="text-[#747684] hover:text-[#1a1b22]">
            <X className="w-4 h-4" />
          </button>
        </div>

        {step === "preview" ? (
          <>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">Message Type</label>
                <select data-testid="loc-whatsapp-type-select" value={messageType} onChange={(e) => changeType(e.target.value)} className={inputCls}>
                  {LOC_WHATSAPP_MESSAGE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">Mobile Number</label>
                <input data-testid="loc-whatsapp-mobile-input" value={mobile} onChange={(e) => setMobile(e.target.value)} className={inputCls} />
              </div>
              {(showParkingStartDate || showLastDateNoCharge) && (
                <div className="grid grid-cols-2 gap-2 bg-amber-50 border border-amber-200 rounded-md p-2.5">
                  <div className="col-span-2">
                    <label className="text-[10.5px] font-semibold uppercase tracking-wider text-amber-700 block mb-1">Parking Charge / Day (₹)</label>
                    <input data-testid="loc-whatsapp-parking-per-day" value={parkingPerDay} onChange={(e) => setParkingPerDay(e.target.value)} className={inputCls} />
                  </div>
                  {showLastDateNoCharge && (
                    <div>
                      <label className="text-[10.5px] font-semibold uppercase tracking-wider text-amber-700 block mb-1">Last Date W/o Charges</label>
                      <input type="date" data-testid="loc-whatsapp-last-date-no-charge" value={lastDateNoCharge} onChange={(e) => setLastDateNoCharge(e.target.value)} className={inputCls} />
                    </div>
                  )}
                  {showParkingStartDate && (
                    <div className={showLastDateNoCharge ? "" : "col-span-2"}>
                      <label className="text-[10.5px] font-semibold uppercase tracking-wider text-amber-700 block mb-1">Charges Start Date</label>
                      <input type="date" data-testid="loc-whatsapp-parking-start-date" value={parkingStartDate} onChange={(e) => setParkingStartDate(e.target.value)} className={inputCls} />
                    </div>
                  )}
                </div>
              )}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684]">Message</label>
                  {!editing && (
                    <button onClick={() => setEditing(true)} data-testid="loc-whatsapp-edit-btn"
                      className="text-[11px] font-semibold text-yamaha-700 hover:underline inline-flex items-center gap-1">
                      <Pencil className="w-3 h-3" /> Edit Message
                    </button>
                  )}
                </div>
                {editing ? (
                  <textarea
                    data-testid="loc-whatsapp-message-textarea"
                    value={message}
                    onChange={(e) => setManualMessage(e.target.value)}
                    rows={7}
                    className={`${inputCls} font-mono`}
                  />
                ) : (
                  <div data-testid="loc-whatsapp-message-preview" className="bg-[#fafafd] border border-[#e2e1eb] rounded-md p-3 text-[12.5px] whitespace-pre-wrap text-[#1a1b22]">
                    {message}
                  </div>
                )}
              </div>
            </div>
            <div className="px-5 py-3 border-t border-[#e2e1eb] flex justify-end gap-2 bg-[#fafafd] rounded-b-xl">
              <button onClick={onClose} data-testid="loc-whatsapp-cancel-btn"
                className="px-3 py-1.5 rounded-md text-[13px] font-medium text-[#444653] hover:bg-[#f0eff7]">
                Cancel
              </button>
              <button onClick={openWhatsapp} data-testid="loc-whatsapp-open-btn"
                className="px-4 py-1.5 rounded-md text-[13px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 inline-flex items-center gap-1.5">
                <MessageCircle className="w-3.5 h-3.5" /> Open WhatsApp
              </button>
            </div>
          </>
        ) : (
          <div className="p-5 space-y-4">
            <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" />
              <div className="text-[12.5px] text-emerald-800">
                WhatsApp opened in a new tab with the message ready. Review it and tap send inside WhatsApp.
              </div>
            </div>
            <div className="text-[12.5px] text-[#444653]">
              Once you've sent it, log it here so it shows in this case's Activity Timeline. You can repeat this for the same message any number of times.
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={onClose} data-testid="loc-whatsapp-confirm-not-now-btn"
                className="px-3 py-1.5 rounded-md text-[13px] font-medium text-[#444653] hover:bg-[#f0eff7]">
                Not Now
              </button>
              <button onClick={markSent} disabled={saving} data-testid="loc-whatsapp-confirm-mark-sent-btn"
                className="px-4 py-1.5 rounded-md text-[13px] font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-60">
                {saving ? "Saving…" : "Mark WhatsApp Sent"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LocWhatsappModal;
